/** Minimum fields before the backend can resolve a rate card (MANUAL / rate-card lines). */
export type RateCardLookupFields = {
  channel?: string;
  product?: string;
  kpi?: string;
  formats?: string[];
  rateSource?: 'MANUAL' | 'PACKAGE' | 'TECH_FEE';
};

export function hasRateCardChannel(channel?: string): boolean {
  return typeof channel === 'string' && channel.trim().length > 0;
}

/**
 * Rate card options / metrics must not run with orphaned filters (e.g. formats only).
 * Package / tech-fee lines only need channel for options; MANUAL lines need channel before cascading filters.
 */
export function buildRateCardOptionsQueryParams(
  fields: RateCardLookupFields & { showDraft?: boolean }
): Record<string, unknown> | null {
  const { channel, product, kpi, formats, rateSource, showDraft = true } = fields;
  const isPackageMode = rateSource === 'PACKAGE' || rateSource === 'TECH_FEE';

  const hasOrphanFilters =
    !hasRateCardChannel(channel) &&
    (!!product?.trim() || !!kpi?.trim() || (!isPackageMode && Array.isArray(formats) && formats.length > 0));

  if (hasOrphanFilters) {
    return null;
  }

  if (!hasRateCardChannel(channel)) {
    return { showDraft };
  }

  return {
    showDraft,
    channel: [channel.trim()],
    ...(product?.trim() ? { product: [product.trim()] } : {}),
    ...(kpi?.trim() ? { kpis: [kpi.trim()] } : {}),
    ...(!isPackageMode && Array.isArray(formats) && formats.length > 0 ? { formats } : {})
  };
}

export type ChannelMetricsFields = RateCardLookupFields & {
  mediaPlanId?: string;
  budget?: number;
  sellRate?: number;
  packageId?: string;
  feePercent?: number;
  feeBaseChannel?: string;
};

/** Whether GET /channels/metrics should run for the current wizard / row state. */
export function canFetchChannelMetrics(fields: ChannelMetricsFields): boolean {
  const {
    mediaPlanId,
    channel,
    kpi,
    budget,
    sellRate,
    rateSource,
    packageId,
    product,
    formats,
    feePercent,
    feeBaseChannel
  } = fields;

  if (!mediaPlanId || !hasRateCardChannel(channel)) {
    return false;
  }

  if (rateSource === 'PACKAGE') {
    return !!kpi?.trim() && !!packageId?.trim() && budget != null && sellRate != null;
  }

  if (rateSource === 'TECH_FEE') {
    return !!feeBaseChannel?.trim() && feePercent != null && Number(feePercent) > 0;
  }

  return (
    !!kpi?.trim() &&
    budget != null &&
    sellRate != null &&
    !!product?.trim() &&
    Array.isArray(formats) &&
    formats.length > 0
  );
}

export function isRateCardNotFoundError(message: unknown): boolean {
  if (typeof message !== 'string') return false;
  const lower = message.toLowerCase();
  return lower.includes('no rate card found') || (lower.includes('notfound') && lower.includes('rate card'));
}
